import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiConfigService } from '../services/api';
import LoginIllustration3D, { LoginLeaves, LoginVase } from '../components/LoginIllustration3D';
import '../styles/LoginModern.css';

const EyeIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {!open && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

const Login = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: credenciais, 2: OTP
  const [tempToken, setTempToken] = useState('');
  const [ultimoAcesso, setUltimoAcesso] = useState(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const tiltEnabled = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOTP } = useAuth();
  const { toggleTheme, isDark } = useTheme();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await apiConfigService.refreshLogoVersion();
      if (!cancelled) setLogoUrl(apiConfigService.getLogoUrl());
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    tiltEnabled.current = !reduce && !coarse;
  }, []);

  // Verificar se vem do setup OTP
  useEffect(() => {
    if (location.state?.showSecurityPopup) {
      // Login já foi completado no OTPSetup, só navegar pro dashboard
      navigate('/dashboard');
    }
  }, [location.state, navigate]);

  const handleMouseMove = useCallback((e) => {
    if (!tiltEnabled.current) return;
    const { innerWidth: w, innerHeight: h } = window;
    setTilt({
      x: ((e.clientY / h) - 0.5) * -5,
      y: ((e.clientX / w) - 0.5) * 6
    });
  }, []);

  const handleMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError('Por favor, preencha todos os campos');
      setLoading(false);
      return;
    }

    try {
      const result = await login(username, password);

      console.log('🔑 Resultado do login no Login.js:', result);

      if (result.success) {
        console.log('✅ Login com sucesso, navegando para dashboard');
        navigate('/dashboard');
      } else if (result.requiresOTPSetup) {
        console.log('🔐 Requer setup de OTP, navegando para /otp/setup');
        // Primeiro acesso - configurar OTP
        localStorage.setItem('tempToken', result.tempToken);
        localStorage.setItem('tempUsername', username);
        navigate('/otp/setup');
      } else if (result.requiresOTP) {
        console.log('🔐 Requer verificação de OTP');
        // OTP já configurado - solicitar código
        setTempToken(result.tempToken);
        setUltimoAcesso({
          ip: result.ultimo_ip_login,
          data: result.ultimo_login_at
        });
        setStep(2);
      } else {
        console.log('❌ Erro no login:', result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error('❌ Erro ao fazer login:', err);
      setError('Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!otpCode || otpCode.length !== 6) {
      setError('Digite um código de 6 dígitos');
      setLoading(false);
      return;
    }

    try {
      const result = await verifyOTP(tempToken, otpCode);

      if (result.success) {
        // Salvar informações de segurança para o popup
        localStorage.setItem('showSecurityPopup', 'true');
        navigate('/dashboard');
      } else {
        setError(result.error || 'Código inválido');
      }
    } catch (err) {
      setError('Erro ao verificar OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setTempToken('');
    setOtpCode('');
    setUltimoAcesso(null);
    setError('');
  };

  const errorBox = error && (
    <div className="lx-error" role="alert">
      <span aria-hidden="true">⚠</span>
      {error}
    </div>
  );

  return (
    <div className="lx-page" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <div className="lx-glow lx-glow--1" />
      <div className="lx-glow lx-glow--2" />
      <LoginVase />
      <LoginLeaves />

      <button
        type="button"
        className="theme-toggle lx-theme-toggle"
        onClick={toggleTheme}
        title={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
        aria-label={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
      >
        <span className="theme-toggle-icon" aria-hidden>{isDark ? '☀' : '☾'}</span>
        <span>{isDark ? 'Claro' : 'Escuro'}</span>
      </button>

      <div className="lx-stage">
        <div
          className="lx-frame"
          style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
        >
          <header className="lx-brand">
            <div className="lx-brand-mark">
              <img
                src={logoUrl || '/logo-extk-onda.png'}
                alt="EXTK e ONDA TELECOM"
                decoding="async"
                onError={() => setLogoUrl('/logo-extk-onda.png')}
              />
            </div>
            <div>
              <h1 className="lx-brand-title">EXTK + ONDA</h1>
              <span className="lx-brand-sub">CRM | TELECOM</span>
            </div>
          </header>

          <div className="lx-card">
            <section className="lx-art">
              <LoginIllustration3D />
              <p className="lx-tagline">
                Conectando clientes,
                <br />
                impulsionando a comunicação.
              </p>
            </section>

            <section className="lx-form-panel">
              {step === 1 ? (
                <form onSubmit={handleStep1} noValidate>
                  <div className="lx-field">
                    <label htmlFor="username">Nome de Usuário / E-mail</label>
                    <input
                      id="username"
                      className="lx-input"
                      type="text"
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                      autoFocus
                    />
                  </div>

                  <div className="lx-field">
                    <label htmlFor="password">Senha</label>
                    <div className="lx-input-wrap">
                      <input
                        id="password"
                        className="lx-input lx-input--password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="lx-eye"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        tabIndex={-1}
                      >
                        <EyeIcon open={showPassword} />
                      </button>
                    </div>
                  </div>

                  {errorBox}

                  <button type="submit" className="lx-btn" disabled={loading}>
                    {loading && <span className="lx-spinner" aria-hidden="true" />}
                    {loading ? 'Entrando...' : 'Entrar'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleStep2} noValidate>
                  <h2 className="lx-form-title">Verificação em duas etapas</h2>
                  <p className="lx-form-subtitle">Digite o código de 6 dígitos do Google Authenticator.</p>

                  <div className="lx-field">
                    <label htmlFor="otpCode">Código de Autenticação</label>
                    <input
                      id="otpCode"
                      className="lx-input lx-input--otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]*"
                      maxLength="6"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      disabled={loading}
                      autoFocus
                    />
                  </div>

                  {ultimoAcesso?.ip && (
                    <div className="lx-security">
                      Último acesso
                      <strong>
                        IP: {ultimoAcesso.ip || 'N/A'}
                        {ultimoAcesso.data && (
                          <> · {new Date(ultimoAcesso.data).toLocaleString('pt-BR')}</>
                        )}
                      </strong>
                    </div>
                  )}

                  {errorBox}

                  <button type="submit" className="lx-btn" disabled={loading}>
                    {loading && <span className="lx-spinner" aria-hidden="true" />}
                    {loading ? 'Verificando...' : 'Verificar'}
                  </button>

                  <button type="button" onClick={handleBack} className="lx-btn lx-btn--ghost" disabled={loading}>
                    ← Voltar
                  </button>
                </form>
              )}
            </section>
          </div>
        </div>

        <p className="lx-footer">© 2026 EXTK e ONDA TELECOM. Todos os direitos reservados.</p>
      </div>
    </div>
  );
};

export default Login;
